import { AppstoreOutlined, BarsOutlined, DownloadOutlined, FilterOutlined } from "@ant-design/icons"
import { Button, Col, Empty, Flex, Radio, Row, Skeleton, Tag } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { AssignDocumentTypeModal } from "../../Files/components/AssignDocumentTypeModal.tsx"
import { ActivityDrawer } from "../../Files/components/ActivityDrawer.tsx"
import { CommentsDrawer } from "../../Files/components/CommentsDrawer.tsx"
import { RequestReviewModal } from "../../Files/components/RequestReviewModal.tsx"
import { ShareDialog } from "../../Files/components/ShareDialog.tsx"
import { UploadNewVersionModal } from "../../Files/components/UploadNewVersionModal.tsx"
import { VersionHistoryDrawer } from "../../Files/components/VersionHistoryDrawer.tsx"
import { documentTypeTagColor } from "../../Files/helpers/document-type-tag-color.ts"
import { useFilesView } from "../../Files/helpers/files-view-preference.ts"
import { fileItemKey, mapDocumentToFileItem } from "../../Files/helpers/map-to-file-item.ts"
import { useFavorites } from "../../Files/hooks/useFavorites.ts"
import { FileCard } from "../../Files/components/FileCard.tsx"
import { type FileOnlyColumn, FilesListView } from "../../Files/components/FilesListView.tsx"
import { FilesSortSelect } from "../../Files/components/FilesSortSelect.tsx"
import { FileViewer } from "../../Files/components/FileViewer.tsx"
import { type FileItem } from "../../Files/types/file-item.types.ts"
import { DEFAULT_SORT_ORDER, type FilesSortOrder } from "../../Files/types/files-filters.types.ts"
import type { ProjectDocument, RelatedRecord } from "../../Project/types/project-documents.types.ts"
import { GlobalLinkRecordModal } from "./GlobalLinkRecordModal.tsx"
import { RenameFileModal } from "../../Project/ProjectViewPage/components/RenameFileModal.tsx"
import { downloadFilesAsZip } from "../../../utils/download-files-zip.ts"
import { EMPTY_GLOBAL_FILES_FILTERS, useGlobalFilesData } from "../hooks/useGlobalFilesData.ts"
import { useGlobalFileActions } from "../hooks/useGlobalFileActions.ts"
import { GlobalFilesActiveFilterChips } from "./GlobalFilesActiveFilterChips.tsx"
import { GlobalFilesFilterDrawer } from "./GlobalFilesFilterDrawer.tsx"
import {
  countActiveGlobalFilesFilters,
  EMPTY_GLOBAL_FILES_FILTER_STATE,
  GLOBAL_FILES_ATTENTION_CATEGORIES,
  type GlobalFilesAttention,
  type GlobalFilesFilters
} from "../types/global-files-filters.types.ts"

const isGlobalFilesAttention = (value: string | null): value is GlobalFilesAttention =>
  value === "needs_attention" ||
  (GLOBAL_FILES_ATTENTION_CATEGORIES as string[]).includes(value ?? "")

interface GlobalFilesAllFilesTabProps {
  search: string
  // Overview's "Need Attention" KPI and Document Health rows both drill in
  // here via `?filter=` — mirrors `GlobalFilesBusinessDocumentsTab`'s own
  // `initialFilter` prop, which is where `?filter=` is actually read from
  // the URL (this tab never reads `useSearchParams` for it itself).
  initialFilter?: string | null
}

export function GlobalFilesAllFilesTab({ search, initialFilter }: GlobalFilesAllFilesTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [view, setView] = useFilesView()
  const [sort, setSort] = useState<FilesSortOrder>(DEFAULT_SORT_ORDER)
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [uiFilters, setUiFilters] = useState<GlobalFilesFilters>(() => {
    const initialDocumentType =
      searchParams.get("filter") === "proposals"
        ? "proposal"
        : searchParams.get("filter") === "contracts"
          ? "contract"
          : null
    return {
      ...EMPTY_GLOBAL_FILES_FILTER_STATE,
      documentType: initialDocumentType,
      attention: isGlobalFilesAttention(initialFilter ?? null) ? (initialFilter as GlobalFilesAttention) : null
    }
  })
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)

  const filters = useMemo(
    () => ({
      ...EMPTY_GLOBAL_FILES_FILTERS,
      q: search,
      documentType: uiFilters.documentType,
      status: uiFilters.status,
      location: uiFilters.location,
      recordId: uiFilters.recordId,
      customerIds: uiFilters.customerIds,
      dateRange: uiFilters.dateRange,
      dateAfter: uiFilters.dateAfter,
      dateBefore: uiFilters.dateBefore,
      attention: uiFilters.attention
    }),
    [search, uiFilters]
  )
  const { items, loading, loadingMore, failed, hasMore, loadMore, reload, rights } =
    useGlobalFilesData("all", filters, sort)

  const favorites = useFavorites("global")

  const navigateToProject = (file: FileItem) => {
    const info = file.projectInfo
    if (!info) return
    navigate(info.is_project ? RoutesPaths.project.view(info.id).overview : RoutesPaths.lead.view(info.id))
  }

  const handleViewRecord = (record: RelatedRecord) => {
    if (record.type === "proposal") navigate(RoutesPaths.proposal.view(record.id))
    else if (record.type === "contract") navigate(RoutesPaths.contract.view(record.id))
    else if (record.type === "project") navigate(RoutesPaths.project.view(record.id).overview)
    else if (record.type === "lead") navigate(RoutesPaths.lead.view(record.id))
    else if (record.type === "invoice" && previewFile?.projectInfo) {
      navigate(RoutesPaths.project.view(previewFile.projectInfo.id).invoices)
    }
  }

  const actions = useGlobalFileActions({
    fileItems: items,
    reload,
    onViewProject: navigateToProject
  })

  // Mirrors `ProjectViewFilesSection.handleVersionUploaded`/`handleVersionRestored`
  // — a new version's row id differs from the one it was favorited/previewed
  // under, so both need to be carried forward explicitly rather than relying
  // on `reload()` + FileViewer's own by-key resync (which can't find a
  // renamed key).
  const handleVersionUploaded = (newVersion: ProjectDocument) => {
    const previousFile = actions.uploadingNewVersionFile
    actions.setUploadingNewVersionFile(null)
    if (previousFile) {
      const updated = mapDocumentToFileItem(newVersion, rights)
      favorites.migrateFavorite(previousFile.key, fileItemKey("document", newVersion.id))
      if (previewFile?.key === previousFile.key) setPreviewFile(updated)
    }
    void reload()
  }

  const handleVersionRestored = async (version: ProjectDocument) => {
    const previousFile = actions.versionHistoryFile
    const restored = await actions.restoreVersion(version.id)
    if (restored && previousFile) {
      const updated = mapDocumentToFileItem(restored, rights)
      favorites.migrateFavorite(previousFile.key, fileItemKey("document", restored.id))
      if (previewFile?.key === previousFile.key) setPreviewFile(updated)
    }
    return !!restored
  }

  const handlePreviewVersion = (document: ProjectDocument) => {
    setPreviewFile(mapDocumentToFileItem(document, rights))
  }

  const extraColumns: FileOnlyColumn[] = [
    {
      title: t("globalFiles.columns.documentType", "Document Type"),
      key: "documentType",
      renderFile: (file: FileItem) =>
        file.documentTypeLabel ? (
          <Tag color={documentTypeTagColor(file.documentType ?? "")}>
            {file.documentTypeLabel}
          </Tag>
        ) : (
          "—"
        )
    },
    {
      // "Location" (the workspace) is a distinct concept from "Related To"
      // (a business-record link, e.g. a Contract) — this column used to be
      // mislabeled "Related To" while showing the workspace. The file's
      // actual Related To (if any) is a separate concern shown in its own
      // details panel, not this list.
      title: t("globalFiles.columns.location", "Location"),
      key: "location",
      renderFile: (file: FileItem) =>
        file.projectInfo ? (
          <Flex vertical gap={0}>
            <span>
              {(file.projectInfo.is_project
                ? t("globalFiles.relatedTo.project", "Project")
                : t("globalFiles.relatedTo.lead", "Lead")) + ` · ${file.projectInfo.uid}`}
            </span>
            {file.projectInfo.name && (
              <span className="text-xs text-text-tertiary">{file.projectInfo.name}</span>
            )}
          </Flex>
        ) : file.isCompanyScoped ? (
          t("globalFiles.location.companyFiles", "Company Files")
        ) : (
          "—"
        )
    },
    {
      title: t("globalFiles.columns.customer", "Customer"),
      key: "customer",
      renderFile: (file: FileItem) => file.projectInfo?.customer_name || "—"
    },
    {
      title: t("globalFiles.columns.uploadedBy", "Uploaded By"),
      key: "uploadedBy",
      renderFile: (file: FileItem) => file.createdByName || "—"
    }
  ]

  const activeFilterCount = countActiveGlobalFilesFilters(uiFilters)

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center" wrap="wrap" gap="small">
        <Flex gap="small" align="center">
          <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerOpen(true)}>
            {t("globalFiles.filters.filterButton", "Filter")}
            {activeFilterCount > 0 && ` (${activeFilterCount})`}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={!items.length}
            onClick={() =>
              downloadFilesAsZip(
                items.map((file) => ({ url: file.fileUrl, name: file.name })),
                t("globalFiles.allFiles.downloadAllZipName", "All Files")
              )
            }
          >
            {t("globalFiles.allFiles.downloadAll", "Download All Files")}
          </Button>
        </Flex>
        <Flex gap="small" align="center">
          <FilesSortSelect value={sort} onChange={setSort} />
          <Radio.Group
            value={view}
            onChange={(e) => setView(e.target.value)}
            optionType="button"
            options={[
              {
                label: (
                  <span aria-label={t("files.view.grid")}>
                    <AppstoreOutlined />
                  </span>
                ),
                value: "grid"
              },
              {
                label: (
                  <span aria-label={t("files.view.list")}>
                    <BarsOutlined />
                  </span>
                ),
                value: "list"
              }
            ]}
          />
        </Flex>
      </Flex>

      <GlobalFilesActiveFilterChips filters={uiFilters} onChange={setUiFilters} />

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : failed ? (
        <Empty description={t("globalFiles.allFiles.loadFailed", "Couldn't load files.")}>
          <Button onClick={() => window.location.reload()}>{t("common.retry", "Retry")}</Button>
        </Empty>
      ) : !items.length ? (
        <Empty
          description={t(
            "globalFiles.allFiles.empty",
            "No files yet. Files uploaded to your accessible projects and leads will appear here."
          )}
        />
      ) : view === "grid" ? (
        <Row gutter={[12, 12]}>
          {items.map((file) => (
            <Col key={file.key} xs={12} sm={8} md={6} lg={4}>
              <FileCard
                file={file}
                onPreview={setPreviewFile}
                actions={actions.fileActions(file)}
                favorited={favorites.isFavorite(file.key)}
                onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <FilesListView
          items={items}
          actions={actions.fileActions}
          onPreview={setPreviewFile}
          isFavorite={(f) => favorites.isFavorite(f.key)}
          onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          extraColumns={extraColumns}
          columnConfigMode="global"
        />
      )}

      {!loading && hasMore && (
        <Flex justify="center">
          <Button loading={loadingMore} onClick={loadMore}>
            {t("globalFiles.allFiles.loadMore", "Load more")}
          </Button>
        </Flex>
      )}

      {previewFile && (
        <FileViewer
          file={previewFile}
          items={items}
          onClose={() => setPreviewFile(null)}
          fileActions={actions.fileActions}
          isFavorite={(f) => favorites.isFavorite(f.key)}
          onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          onViewProject={navigateToProject}
          onRename={(f) => actions.setRenamingFile(f)}
          documentTypeOptions={actions.documentTypeOptions}
          documentTypesFailed={actions.documentTypesFailed}
          onChangeDocumentType={actions.updateDocumentType}
          onLinkRecord={(f) => actions.setLinkingFile(f)}
          onViewRecord={handleViewRecord}
          onUploadNewVersion={(f) => actions.setUploadingNewVersionFile(f)}
          onViewVersionHistory={(f) => actions.setVersionHistoryFile(f)}
          statusOptions={actions.statusOptions}
          statusesFailed={actions.statusesFailed}
          onChangeStatus={actions.updateStatus}
          onRequestReview={(f) => actions.setRequestReviewFile(f)}
          onViewComments={(f) => actions.setCommentsFile(f)}
          onViewActivity={(f) => actions.setActivityFile(f)}
          onShare={(f) => actions.setSharingFile(f)}
          onToggleCustomerVisibility={actions.updateCustomerVisibility}
        />
      )}

      {actions.sharingFile && (
        <ShareDialog file={actions.sharingFile} onClose={() => actions.setSharingFile(null)} />
      )}

      {actions.linkingFile && (
        <GlobalLinkRecordModal
          currentRecord={actions.linkingFile.relatedRecord ?? null}
          onClose={() => actions.setLinkingFile(null)}
          onSubmit={actions.linkRecord}
          onUnlink={actions.unlinkRecord}
        />
      )}

      {actions.uploadingNewVersionFile && (
        <UploadNewVersionModal
          projectId={actions.uploadingNewVersionFile.projectInfo?.id}
          file={actions.uploadingNewVersionFile}
          onClose={() => actions.setUploadingNewVersionFile(null)}
          onUploaded={handleVersionUploaded}
        />
      )}

      {actions.versionHistoryFile && (
        <VersionHistoryDrawer
          open
          fileName={actions.versionHistoryFile.name}
          documentId={actions.versionHistoryFile.id}
          onClose={() => actions.setVersionHistoryFile(null)}
          onPreviewVersion={handlePreviewVersion}
          onRestore={handleVersionRestored}
        />
      )}

      {actions.renamingFile && (
        <RenameFileModal
          fileName={actions.renamingFile.name}
          onClose={() => actions.setRenamingFile(null)}
          onSubmit={(next) => actions.renameFile(actions.renamingFile as FileItem, next)}
        />
      )}

      {actions.assigningDocumentTypeFile && (
        <AssignDocumentTypeModal
          file={actions.assigningDocumentTypeFile}
          options={actions.documentTypeOptions}
          loadFailed={actions.documentTypesFailed}
          onClose={() => actions.setAssigningDocumentTypeFile(null)}
          onSubmit={(value) =>
            actions.updateDocumentType(actions.assigningDocumentTypeFile as FileItem, value)
          }
        />
      )}

      {actions.requestReviewFile && (
        <RequestReviewModal
          file={actions.requestReviewFile}
          onClose={() => actions.setRequestReviewFile(null)}
          onSubmit={actions.submitRequestReview}
        />
      )}

      {actions.commentsFile && (
        <CommentsDrawer file={actions.commentsFile} onClose={() => actions.setCommentsFile(null)} />
      )}

      {actions.activityFile && (
        <ActivityDrawer file={actions.activityFile} onClose={() => actions.setActivityFile(null)} />
      )}

      <GlobalFilesFilterDrawer
        open={filterDrawerOpen}
        filters={uiFilters}
        onClose={() => setFilterDrawerOpen(false)}
        onChange={setUiFilters}
      />
    </Flex>
  )
}
